import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import axios from 'axios';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Upload,
  X,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Package,
  GripVertical,
  ArrowLeft,
  Save
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { uploadImage } from '../../lib/uploadImage';
import ReactQuill from '../../components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 12;

const getImageUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }

  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

// Transliterate Cyrillic (Russian) text to Latin and produce a URL-safe slug.
const CYRILLIC_MAP = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya'
};

const slugifyRu = (text = '') => {
  if (!text) return '';
  const lower = text.toString().toLowerCase().trim();
  let out = '';
  for (const ch of lower) {
    if (CYRILLIC_MAP[ch] !== undefined) {
      out += CYRILLIC_MAP[ch];
    } else {
      out += ch;
    }
  }
  return out
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const emptyFormData = {
  name: '',
  nameRu: '',
  description: '',
  descriptionRu: '',
  price: '',
  originalPrice: '',
  promoStart: '',
  promoEnd: '',
  category: '',
  categories: [],
  brandId: '',
  storeName: '',
  storeNameRu: '',
  image: '',
  images: [],
  available: 100,
  sku: '',
  badge: '',
  badgeRu: '',
  seoTitle: '',
  seoTitleRu: '',
  seoDescription: '',
  seoDescriptionRu: '',
  seoKeywords: '',
  slug: '',
  slugRu: '',
  galleryCaption: '',
  galleryCaptionRu: '',
  faqs: []
};

const ProductsManagement = () => {
  const { getAuthHeaders } = useAdmin();

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [loading, setLoading] = useState(true);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('ro');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);

  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  // Tracks whether the admin manually edited slugRu; when false we auto-fill it from nameRu.
  const [slugRuEdited, setSlugRuEdited] = useState(false);

  const [formData, setFormData] = useState(emptyFormData);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE
      });

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      const response = await axios.get(
        `${API}/admin/products?${params.toString()}`,
        getAuthHeaders()
      );

      setProducts(response.data.items || []);
      setTotalProducts(response.data.total || 0);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca produsele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const allOnPageSelected =
    products.length > 0 && products.every((p) => selectedIds.has(p.id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allOnPageSelected) {
        products.forEach((p) => next.delete(p.id));
      } else {
        products.forEach((p) => next.add(p.id));
      }

      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);

    if (ids.length === 0) return;

    if (!window.confirm(`Sigur doriți să ștergeți ${ids.length} produse selectate?`)) return;

    try {
      setBulkDeleting(true);

      const res = await axios.post(
        `${API}/admin/products/bulk-delete`,
        { ids },
        getAuthHeaders()
      );

      toast({
        title: 'Succes',
        description: res.data.message || `${ids.length} produse șterse`
      });

      clearSelection();

      const newTotal = Math.max(totalProducts - ids.length, 0);
      const newLastPage = Math.max(1, Math.ceil(newTotal / ITEMS_PER_PAGE));

      if (currentPage > newLastPage) {
        setCurrentPage(newLastPage);
      } else {
        fetchProducts();
      }
    } catch (err) {
      toast({
        title: 'Eroare',
        description: err.response?.data?.detail || 'Nu s-au putut șterge produsele',
        variant: 'destructive'
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateForm = () => {
    setEditingProduct(null);
    resetForm();
    setActiveTab('ro');
    setIsProductFormOpen(true);
  };

  const closeProductForm = () => {
    setIsProductFormOpen(false);
    setEditingProduct(null);
    setActiveTab('ro');
    resetForm();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }
    try {
      const url = await uploadImage(file, getAuthHeaders());
      setImagePreview(url);
      setFormData((prev) => ({ ...prev, image: url }));
    } catch (err) {
      toast({ title: 'Eroare', description: 'Nu s-a putut încărca imaginea', variant: 'destructive' });
    }
    e.target.value = '';
  };

  const handleMultipleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const url = await uploadImage(file, getAuthHeaders());
        setImagePreviews((prev) => [...prev, url]);
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, url]
        }));
      } catch (err) {
        toast({ title: 'Eroare', description: `Nu s-a putut încărca ${file.name}`, variant: 'destructive' });
      }
    }
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));

    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setImagePreviews((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    setFormData((prev) => {
      const nextImages = [...prev.images];
      const [moved] = nextImages.splice(fromIndex, 1);
      nextImages.splice(toIndex, 0, moved);

      return {
        ...prev,
        images: nextImages
      };
    });
  };

  const handleGalleryDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleGalleryImageDrop = (e, targetIndex) => {
    e.preventDefault();

    const sourceIndexFromTransfer = Number(e.dataTransfer.getData('text/plain'));
    const sourceIndex =
      Number.isInteger(sourceIndexFromTransfer) && sourceIndexFromTransfer >= 0
        ? sourceIndexFromTransfer
        : draggedImageIndex;

    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDraggedImageIndex(null);
      return;
    }

    moveGalleryImage(sourceIndex, targetIndex);
    setDraggedImageIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const hasOriginalPrice =
        formData.originalPrice !== '' &&
        formData.originalPrice !== null &&
        formData.originalPrice !== undefined;

      const originalPriceNum = hasOriginalPrice
        ? parseFloat(formData.originalPrice)
        : null;

      const priceNum = parseFloat(formData.price);

      // Validare date promoție
      if (formData.promoStart && formData.promoEnd && formData.promoEnd < formData.promoStart) {
        toast({
          title: 'Eroare',
          description: 'Data de sfârșit a promoției nu poate fi înainte de data de început',
          variant: 'destructive'
        });
        setActiveTab('ro');
        return;
      }

      if ((formData.promoStart || formData.promoEnd) && !hasOriginalPrice) {
        toast({
          title: 'Eroare',
          description: 'Pentru a seta perioada promoției, completează și Prețul Original (prețul întreg)',
          variant: 'destructive'
        });
        setActiveTab('ro');
        return;
      }

      const productData = {
        ...formData,
        slug: formData.slug,
        slugRu: formData.slugRu,
        price: priceNum,
        originalPrice: originalPriceNum,
        promoStart: formData.promoStart || '',
        promoEnd: formData.promoEnd || '',
        available: Number(formData.available),
        discount:
          originalPriceNum && originalPriceNum > priceNum
            ? Math.round(((originalPriceNum - priceNum) / originalPriceNum) * 100)
            : 0,
        colors: ['#9b59b6', '#3498db', '#e74c3c', '#f1c40f'],
        sizes: ['S', 'M', 'L', 'XL', 'XXL'],
        specifications: specifications
          .filter((spec) => spec.title && spec.value)
          .map((spec) => ({
            title: spec.title,
            value: spec.value,
            titleRu: spec.titleRu || '',
            valueRu: spec.valueRu || ''
          })),
        faqs: faqs
          .filter((faq) => faq.question && faq.answer)
          .map((faq) => ({
            question: faq.question,
            answer: faq.answer,
            questionRu: faq.questionRu || '',
            answerRu: faq.answerRu || '',
          })),
        rating: 4.5,
        reviews: 100,
        sold: 0,
        inStock: true
      };

      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          productData,
          getAuthHeaders()
        );

        toast({
          title: 'Succes',
          description: 'Produsul a fost actualizat!'
        });
      } else {
        await axios.post(`${API}/products`, productData, getAuthHeaders());

        toast({
          title: 'Succes',
          description: 'Produsul a fost creat!'
        });
      }

      closeProductForm();
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut salva produsul',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest produs?')) return;

    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeaders());

      toast({
        title: 'Succes',
        description: 'Produsul a fost șters!'
      });

      fetchProducts();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut șterge produsul',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);

    setFormData({
      name: product.name || '',
      nameRu: product.nameRu || '',
      description: product.description || '',
      descriptionRu: product.descriptionRu || '',
      price: product.price ?? '',
      originalPrice: product.originalPrice ?? '',
      promoStart: product.promoStart || '',
      promoEnd: product.promoEnd || '',
      category: product.category || '',
      categories: product.categories || [],
      brandId: product.brandId || '',
      storeName: product.storeName || '',
      storeNameRu: product.storeNameRu || '',
      image: product.image || '',
      images: product.images || [],
      available: product.available ?? 100,
      sku: product.sku || '',
      badge: product.badge || '',
      badgeRu: product.badgeRu || '',
      seoTitle: product.seoTitle || '',
      seoTitleRu: product.seoTitleRu || '',
      seoDescription: product.seoDescription || '',
      seoDescriptionRu: product.seoDescriptionRu || '',
      seoKeywords: product.seoKeywords || '',
      slug: product.slug || '',
      slugRu: product.slugRu || '',
      galleryCaption: product.galleryCaption || '',
      galleryCaptionRu: product.galleryCaptionRu || '',
    });

    setSpecifications(product.specifications || []);
    setFaqs(product.faqs || []);
    setImagePreview(product.image || null);
    setImagePreviews(product.images || []);
    setActiveTab('ro');
    // Consider slugRu as "manually set" if it differs from what auto-gen would
    // produce from nameRu — admin can click "Regenerează" to opt back in.
    const autoFromName = slugifyRu(product.nameRu || '');
    setSlugRuEdited(!!product.slugRu && product.slugRu !== autoFromName);
    setIsProductFormOpen(true);
  };

  const resetForm = () => {
    setFormData(emptyFormData);
    setImagePreview(null);
    setImagePreviews([]);
    setSpecifications([]);
    setFaqs([]);
    setDraggedImageIndex(null);
    setSlugRuEdited(false);
  };

  const addSpecification = () => {
    setSpecifications([
      ...specifications,
      { title: '', value: '', titleRu: '', valueRu: '' }
    ]);
  };

  const updateSpecification = (index, field, value) => {
    const newSpecs = [...specifications];
    newSpecs[index][field] = value;
    setSpecifications(newSpecs);
  };

  const removeSpecification = (index) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const addFaq = () => {
    setFaqs([
      ...faqs,
      { question: '', answer: '', questionRu: '', answerRu: '' }
    ]);
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const totalPages = Math.max(1, Math.ceil(totalProducts / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + products.length;
  const currentProducts = products;

  const PAGE_GROUP_SIZE = 5;
  const currentGroup = Math.floor((currentPage - 1) / PAGE_GROUP_SIZE);
  const groupStart = currentGroup * PAGE_GROUP_SIZE + 1;
  const groupEnd = Math.min(groupStart + PAGE_GROUP_SIZE - 1, totalPages);

  const visiblePages = [];

  for (let page = groupStart; page <= groupEnd; page++) {
    visiblePages.push(page);
  }

  const collectDescendants = (parentId, depth = 1) => {
    const directChildren = categories.filter((c) => c.parentId === parentId);

    let result = [];

    for (const child of directChildren) {
      result.push({ ...child, depth });
      result = result.concat(collectDescendants(child.id, depth + 1));
    }

    return result;
  };

  const renderCategorySection = () => {
    const parent = categories.find(
      (c) => c.name === formData.category && !c.parentId
    );

    const descendants = parent ? collectDescendants(parent.id) : [];

    return (
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Categorie Principală *
          </label>

          <select
            required
            value={formData.category}
            onChange={(e) =>
              setFormData({
                ...formData,
                category: e.target.value,
                categories: []
              })
            }
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
          >
            <option value="">Selectează</option>
            {categories
              .filter((cat) => !cat.parentId)
              .map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
          </select>
        </div>

        {!formData.category && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Categorii Adiționale
            </label>

            <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 text-sm text-gray-500 italic">
              Selectează mai întâi o Categorie Principală pentru a vedea sub-categoriile disponibile.
            </div>
          </div>
        )}

        {formData.category && descendants.length === 0 && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Categorii Adiționale
            </label>

            <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 text-sm text-gray-500 italic">
              Categoria „{formData.category}" nu are sub-categorii definite.
            </div>
          </div>
        )}

        {formData.category && descendants.length > 0 && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Categorii Adiționale — sub-categorii din „{formData.category}"
            </label>

            <div className="border-2 border-gray-200 rounded-xl p-3 max-h-60 overflow-y-auto bg-gray-50">
              {descendants.map((cat) => {
                const isChecked = formData.categories?.includes(cat.name) || false;

                return (
                  <label
                    key={cat.id}
                    className={`flex items-center gap-2 py-1.5 cursor-pointer hover:bg-white px-2 rounded ${
                      cat.depth === 1
                        ? 'font-semibold text-gray-800'
                        : cat.depth === 2
                          ? 'text-gray-700'
                          : 'text-gray-600'
                    }`}
                    style={{ paddingLeft: `${8 + (cat.depth - 1) * 22}px` }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`prod-subcategory-${cat.id}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const currentCategories = formData.categories || [];

                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            categories: [...currentCategories, cat.name]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            categories: currentCategories.filter(
                              (c) => c !== cat.name
                            )
                          });
                        }
                      }}
                      className="w-4 h-4 text-[#a7cf26] rounded focus:ring-[#a7cf26]"
                    />

                    <span className="text-sm">
                      {cat.depth > 1 && (
                        <span className="text-gray-400 mr-1">
                          {'↳ '.repeat(cat.depth - 1)}
                        </span>
                      )}
                      {cat.name}
                    </span>
                  </label>
                );
              })}
            </div>

            <p className="text-xs text-gray-500 mt-1">
              Produsul va apărea în categoria principală + toate sub-categoriile bifate.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Brand
          </label>

          <select
            value={formData.brandId}
            onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
          >
            <option value="">Fără brand</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderImageSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Imagine Produs *
        </label>

        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#a7cf26] transition">
          {imagePreview ? (
            <div className="relative">
              <img
                src={getImageUrl(imagePreview)}
                alt="Preview"
                className="max-h-48 mx-auto rounded-lg"
              />

              <button
                type="button"
                onClick={() => {
                  setImagePreview(null);
                  setFormData({ ...formData, image: '' });
                }}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />

              <label className="cursor-pointer">
                <span className="bg-[#a7cf26] text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 hover:bg-[#96bc21] transition font-semibold">
                  <Upload className="w-5 h-5" />
                  Încarcă Imagine
                </span>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              <p className="text-sm text-gray-500 mt-2">
                PNG, JPG până la 10MB
              </p>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Imagini Suplimentare
        </label>

        <div className="space-y-3">
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {imagePreviews.map((img, index) => (
                <div
                  key={`${index}-${img.slice(0, 30)}`}
                  draggable
                  onDragStart={(e) => handleGalleryDragStart(e, index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => handleGalleryImageDrop(e, index)}
                  onDragEnd={() => setDraggedImageIndex(null)}
                  className={`relative group cursor-grab active:cursor-grabbing rounded-lg ${
                    draggedImageIndex === index ? 'opacity-50 ring-2 ring-[#a7cf26]' : ''
                  }`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Preview ${index + 1}`}
                    draggable={false}
                    className="w-full h-24 object-cover rounded-lg pointer-events-none select-none"
                  />

                  <div className="absolute left-2 top-2 bg-black/55 text-white rounded-full p-1">
                    <GripVertical className="w-3 h-3" />
                  </div>

                  <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                    {index + 1}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    draggable={false}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="cursor-pointer block">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#a7cf26] transition">
              <Plus className="w-8 h-8 text-gray-400 mx-auto mb-2" />

              <span className="text-sm text-gray-600">
                Adaugă imagini ({imagePreviews.length})
              </span>
            </div>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleMultipleImagesUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>
    </div>
  );

  const renderSpecifications = (language) => (
    <div className="border-t pt-6">
      <div className="flex justify-between items-center mb-4">
        <label className="block text-sm font-bold text-gray-700">
          Specificații Custom {language === 'ru' ? '(RU)' : '(RO)'}
        </label>

        <button
          type="button"
          onClick={addSpecification}
          className="text-[#a7cf26] hover:text-[#96bc21] font-semibold text-sm flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Adaugă Specificație
        </button>
      </div>

      {specifications.length > 0 ? (
        <div className="space-y-3">
          {specifications.map((spec, index) => (
            <div
              key={index}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-3 space-y-3"
            >
              {language === 'ro' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Titlu RO (ex: Material)"
                    value={spec.title}
                    onChange={(e) =>
                      updateSpecification(index, 'title', e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />

                  <input
                    type="text"
                    placeholder="Valoare RO (ex: Bumbac 100%)"
                    value={spec.value}
                    onChange={(e) =>
                      updateSpecification(index, 'value', e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Titlu RU (ex: Материал)"
                    value={spec.titleRu || ''}
                    onChange={(e) =>
                      updateSpecification(index, 'titleRu', e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />

                  <input
                    type="text"
                    placeholder="Valoare RU (ex: 100% хлопок)"
                    value={spec.valueRu || ''}
                    onChange={(e) =>
                      updateSpecification(index, 'valueRu', e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => removeSpecification(index)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                <X className="w-4 h-4" />
                Șterge specificația
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">
          Nicio specificație adăugată
        </p>
      )}
    </div>
  );

  if (loading && !isProductFormOpen) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-brand-600"></div>
      </div>
    );
  }

  if (isProductFormOpen) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={closeProductForm}
                className="w-11 h-11 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-700 flex items-center justify-center transition"
                title="Înapoi"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  {editingProduct ? 'Editează produs' : 'Adaugă produs nou'}
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  Completează informațiile produsului pe taburi: RO, RU și SEO.
                </p>
              </div>
            </div>

            <button
              type="submit"
              form="product-form"
              className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-semibold shadow-md shadow-brand-200 transition"
            >
              <Save className="w-4 h-4" />
              {editingProduct ? 'Actualizează produsul' : 'Salvează produsul'}
            </button>
          </div>
        </div>

        <form
          id="product-form"
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden"
        >
          <div className="border-b border-gray-100 px-6 pt-5">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'ro', label: 'RO' },
                { id: 'ru', label: 'RU' },
                { id: 'seo', label: 'SEO' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-3 rounded-t-2xl text-sm font-bold transition ${
                    activeTab === tab.id
                      ? 'bg-brand-500 text-white shadow-md shadow-brand-100'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {activeTab === 'ro' && (
              <div className="space-y-6">
                {renderImageSection()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Nume Produs (RO) *
                    </label>

                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="Nume în română"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Cod produs / SKU
                    </label>

                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) =>
                        setFormData({ ...formData, sku: e.target.value })
                      }
                      placeholder="Se generează automat dacă îl lași gol"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>
                </div>

                {renderCategorySection()}

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Descriere (RO)
                  </label>

                  <ReactQuill
                    value={formData.description}
                    onChange={(value) =>
                      setFormData({ ...formData, description: value })
                    }
                    placeholder="Descriere în română"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Preț (MDL) *
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Preț Original (MDL)
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      value={formData.originalPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, originalPrice: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Stoc *
                    </label>

                    <input
                      type="number"
                      required
                      value={formData.available}
                      onChange={(e) =>
                        setFormData({ ...formData, available: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>
                </div>

                {/* PERIOADĂ PROMOȚIE */}
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Început promoție
                      </label>

                      <input
                        type="date"
                        value={formData.promoStart}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            promoStart: value,
                            promoEnd:
                              prev.promoEnd && value && prev.promoEnd < value
                                ? value
                                : prev.promoEnd
                          }));
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Sfârșit promoție
                      </label>

                      <input
                        type="date"
                        value={formData.promoEnd}
                        min={formData.promoStart || undefined}
                        onChange={(e) =>
                          setFormData({ ...formData, promoEnd: e.target.value })
                        }
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      />
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-gray-500">
                      Prețul redus se aplică doar între aceste date (inclusiv). Necesită
                      Preț Original completat. Fără date, reducerea rămâne activă permanent.
                    </p>

                    {(formData.promoStart || formData.promoEnd) && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, promoStart: '', promoEnd: '' })
                        }
                        className="text-xs font-semibold text-red-600 hover:underline"
                      >
                        Șterge datele
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Magazin
                    </label>

                    <input
                      type="text"
                      value={formData.storeName}
                      onChange={(e) =>
                        setFormData({ ...formData, storeName: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Badge (RO)
                    </label>

                    <input
                      type="text"
                      value={formData.badge}
                      onChange={(e) =>
                        setFormData({ ...formData, badge: e.target.value })
                      }
                      placeholder="ex: REDUCERE, 15% OFF"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>
                </div>

                {renderSpecifications('ro')}
              </div>
            )}

            {activeTab === 'ru' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Nume Produs (RU)
                    </label>

                    <input
                      type="text"
                      value={formData.nameRu}
                      onChange={(e) => {
                        const nameRu = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          nameRu,
                          slugRu: slugRuEdited ? prev.slugRu : slugifyRu(nameRu),
                        }));
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="Название на русском"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Magazin (RU)
                    </label>

                    <input
                      type="text"
                      value={formData.storeNameRu}
                      onChange={(e) =>
                        setFormData({ ...formData, storeNameRu: e.target.value })
                      }
                      placeholder="ex: Магазин"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Descriere (RU)
                  </label>

                  <ReactQuill
                    value={formData.descriptionRu}
                    onChange={(value) =>
                      setFormData({ ...formData, descriptionRu: value })
                    }
                    placeholder="Описание на русском"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Badge (RU)
                  </label>

                  <input
                    type="text"
                    value={formData.badgeRu}
                    onChange={(e) =>
                      setFormData({ ...formData, badgeRu: e.target.value })
                    }
                    placeholder="напр: СКИДКА, 15% OFF"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />
                </div>

                {renderSpecifications('ru')}
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Slug URL
                  </label>

                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    placeholder="ex: tricou-bumbac-premium"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Slug URL (RU)
                  </label>

                  <input
                    type="text"
                    value={formData.slugRu}
                    onChange={(e) => {
                      setSlugRuEdited(true);
                      setFormData({ ...formData, slugRu: e.target.value });
                    }}
                    placeholder="ex: tricou-bumbac-rus"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                  />

                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs text-gray-500">
                      Obligatoriu de pus pentru link RU
                    </p>
                    {slugRuEdited && formData.nameRu && (
                      <button
                        type="button"
                        onClick={() => {
                          setSlugRuEdited(false);
                          setFormData((prev) => ({ ...prev, slugRu: slugifyRu(prev.nameRu) }));
                        }}
                        className="text-xs font-semibold text-[#a7cf26] hover:underline"
                      >
                        Regenerează din nume
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      SEO Title (RO)
                    </label>

                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) =>
                        setFormData({ ...formData, seoTitle: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="Titlu pentru Google"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      SEO Title (RU)
                    </label>

                    <input
                      type="text"
                      value={formData.seoTitleRu}
                      onChange={(e) =>
                        setFormData({ ...formData, seoTitleRu: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="SEO заголовок"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Meta Description (RO)
                    </label>

                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seoDescription: e.target.value
                        })
                      }
                      rows="5"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="Descriere scurtă pentru motoarele de căutare"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Meta Description (RU)
                    </label>

                    <textarea
                      value={formData.seoDescriptionRu}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seoDescriptionRu: e.target.value
                        })
                      }
                      rows="5"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                      placeholder="Краткое SEO описание"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    SEO Keywords
                  </label>

                  <input
                    type="text"
                    value={formData.seoKeywords}
                    onChange={(e) =>
                      setFormData({ ...formData, seoKeywords: e.target.value })
                    }
                    placeholder="ex: haine, tricou, bumbac, Moldova"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                  />
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-bold text-gray-700">
                      FAQ produs
                    </label>

                    <button
                      type="button"
                      onClick={addFaq}
                      className="text-[#a7cf26] hover:text-[#96bc21] font-semibold text-sm flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Adaugă întrebare
                    </button>
                  </div>

                  {faqs.length > 0 ? (
                    <div className="space-y-3">
                      {faqs.map((faq, index) => (
                        <div
                          key={index}
                          className="rounded-2xl border border-gray-200 bg-gray-50 p-3 space-y-3"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Întrebare RO"
                              value={faq.question}
                              onChange={(e) => updateFaq(index, 'question', e.target.value)}
                              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                            />

                            <input
                              type="text"
                              placeholder="Întrebare RU"
                              value={faq.questionRu || ''}
                              onChange={(e) => updateFaq(index, 'questionRu', e.target.value)}
                              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <textarea
                              rows="3"
                              placeholder="Răspuns RO"
                              value={faq.answer}
                              onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                            />

                            <textarea
                              rows="3"
                              placeholder="Răspuns RU"
                              value={faq.answerRu || ''}
                              onChange={(e) => updateFaq(index, 'answerRu', e.target.value)}
                              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFaq(index)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <X className="w-4 h-4" />
                            Șterge întrebarea
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Nicio întrebare adăugată
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50/60 px-6 py-4 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={closeProductForm}
              className="px-5 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-semibold"
            >
              Anulează
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition font-semibold shadow-md shadow-brand-200"
            >
              <Save className="w-4 h-4" />
              {editingProduct ? 'Actualizează' : 'Creează'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Gestionare Produse
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Total:{' '}
              <span className="font-semibold text-gray-800">
                {totalProducts}
              </span>{' '}
              produse în catalog
            </p>
          </div>

          <button
            onClick={openCreateForm}
            data-testid="add-product-btn"
            className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-semibold shadow-md shadow-brand-200 transition"
          >
            <Plus className="w-4 h-4" />
            Adaugă Produs
          </button>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

            <input
              type="text"
              placeholder="Caută produse după nume…"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 focus:bg-white transition"
              data-testid="products-search-input"
            />
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 bg-brand-50 border border-brand-100 rounded-2xl px-4 py-3">
            <span className="text-sm font-semibold text-brand-800">
              {selectedIds.size} produs{selectedIds.size === 1 ? '' : 'e'} selectat
              {selectedIds.size === 1 ? '' : 'e'}
            </span>

            <div className="flex gap-2">
              <button
                onClick={clearSelection}
                className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition font-semibold"
                data-testid="bulk-clear-selection-btn"
              >
                Anulează selecție
              </button>

              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm bg-rose-500 text-white rounded-full hover:bg-rose-600 transition font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                {bulkDeleting ? 'Se șterge…' : `Șterge ${selectedIds.size}`}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-gray-500 text-[11px] uppercase tracking-wider">
                <th className="px-4 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    className="w-4 h-4 accent-brand-500 cursor-pointer"
                    data-testid="select-all-page-checkbox"
                    title={
                      allOnPageSelected
                        ? 'Deselectează pagina'
                        : 'Selectează toată pagina'
                    }
                  />
                </th>

                <th className="px-4 py-4 font-semibold">Produs</th>
                <th className="px-4 py-4 font-semibold">Categorie</th>
                <th className="px-4 py-4 font-semibold">Preț</th>
                <th className="px-4 py-4 font-semibold">Stoc</th>
                <th className="px-4 py-4 font-semibold text-right pr-6">
                  Acțiuni
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {currentProducts.map((product) => (
                <tr
                  key={product.id}
                  className={`hover:bg-brand-50/40 transition ${
                    selectedIds.has(product.id) ? 'bg-brand-50/60' : 'bg-white'
                  }`}
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      className="w-4 h-4 accent-brand-500 cursor-pointer"
                      data-testid={`select-product-${product.id}`}
                    />
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                        {product.image || product.images?.[0] ? (
                          <img
                            src={getImageUrl(product.image || product.images?.[0])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-5 h-5 text-gray-400" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate max-w-[280px]">
                          {product.name}
                        </div>

                        <div className="text-xs text-gray-500 truncate max-w-[280px]">
                          {product.storeName || product.sku || '—'}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4 text-sm text-gray-700">
                    {product.category || '—'}
                  </td>

                  <td className="px-4 py-4">
                    <div className="font-bold text-gray-900">
                      {Number(product.price).toLocaleString('ro-RO')} MDL
                    </div>

                    {product.originalPrice &&
                      Number(product.originalPrice) > Number(product.price) && (
                        <div className="text-xs text-gray-400 line-through">
                          {Number(product.originalPrice).toLocaleString('ro-RO')} MDL
                        </div>
                      )}

                    {(product.promoStart || product.promoEnd) && (
                      <div className="text-[11px] text-brand-700 mt-0.5">
                        {product.promoStart || '…'} → {product.promoEnd || '…'}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                        product.available > 50
                          ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100'
                          : product.available > 10
                            ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                            : 'bg-rose-50 text-rose-700 ring-1 ring-rose-100'
                      }`}
                    >
                      {product.available ?? 0}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex gap-2 justify-end pr-2">
                      <button
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-product-${product.id}`}
                        title="Editează"
                        className="p-2.5 rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 transition"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(product.id)}
                        data-testid={`delete-product-${product.id}`}
                        title="Șterge"
                        className="p-2.5 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {currentProducts.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                    Nu există produse de afișat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50/60 border-t border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Afișare{' '}
            <span className="font-semibold text-gray-800">
              {totalProducts === 0 ? 0 : startIndex + 1}
            </span>{' '}
            –{' '}
            <span className="font-semibold text-gray-800">
              {Math.min(endIndex, totalProducts)}
            </span>{' '}
            din{' '}
            <span className="font-semibold text-gray-800">
              {totalProducts}
            </span>
          </div>

          {totalPages > 1 && (
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setCurrentPage(Math.max(1, groupStart - PAGE_GROUP_SIZE))
                }
                disabled={groupStart === 1}
                className="px-3 py-2 border border-gray-200 rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </button>

              <div className="flex gap-1">
                {visiblePages.map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-full text-sm font-semibold transition ${
                      currentPage === page
                        ? 'bg-brand-500 text-white shadow-md shadow-brand-200'
                        : 'border border-gray-200 hover:bg-white text-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, groupEnd + 1))}
                disabled={groupEnd >= totalPages}
                className="px-3 py-2 border border-gray-200 rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
              >
                Următor
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsManagement;